<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useStore } from 'dashboard/composables/store';
import AsaasIntegrationAPI from 'dashboard/api/asaasIntegration';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import BankLogo from './BankLogo.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const { t } = useI18n();
const store = useStore();
const { isAdmin } = useAdmin();

const isLoading = ref(false);
const isTesting = ref(false);
const isSaving = ref(false);
const isRemoving = ref(false);
const hook = ref(null);
const removeDialogRef = ref(null);

const form = reactive({
  apiKey: '',
  environment: 'production',
  enabled: true,
  features: {
    consultar_cobrancas: true,
    enviar_segunda_via: false,
  },
});

const FEATURE_KEYS = ['consultar_cobrancas', 'enviar_segunda_via'];

const environmentOptions = computed(() => [
  {
    value: 'production',
    label: t('INTEGRATION_SETTINGS.ASAAS.ENVIRONMENT_PRODUCTION'),
  },
  {
    value: 'sandbox',
    label: t('INTEGRATION_SETTINGS.ASAAS.ENVIRONMENT_SANDBOX'),
  },
]);

const featureOptions = computed(() => [
  {
    key: 'consultar_cobrancas',
    label: t('INTEGRATION_SETTINGS.ASAAS.FEATURE_CONSULTAR_COBRANCAS'),
    help: t('INTEGRATION_SETTINGS.ASAAS.FEATURE_CONSULTAR_COBRANCAS_HELP'),
  },
  {
    key: 'enviar_segunda_via',
    label: t('INTEGRATION_SETTINGS.ASAAS.FEATURE_ENVIAR_SEGUNDA_VIA'),
    help: t('INTEGRATION_SETTINGS.ASAAS.FEATURE_ENVIAR_SEGUNDA_VIA_HELP'),
  },
]);

const apiKeyConfigured = computed(() =>
  Boolean(hook.value?.settings?.api_key_configured)
);
const isConnected = computed(() =>
  Boolean(hook.value?.settings?.connection_validated)
);
const isBusy = computed(
  () => isTesting.value || isSaving.value || isRemoving.value
);

const validatedAtLabel = computed(() => {
  const value = hook.value?.settings?.validated_at;
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return t('INTEGRATION_SETTINGS.ASAAS.STATUS_VALIDATED_AT', {
    date: date.toLocaleString(),
  });
});

const errorMessage = (error, fallback) =>
  error?.response?.data?.error || error?.response?.data?.message || fallback;

const applyHook = data => {
  hook.value = data || null;
  const settings = data?.settings || {};
  form.apiKey = '';
  form.environment = settings.environment || 'production';
  form.enabled = data ? Boolean(data.status) : true;
  FEATURE_KEYS.forEach(key => {
    if (settings.features && key in settings.features) {
      form.features[key] = Boolean(settings.features[key]);
    }
  });
};

const refreshIntegrationsList = () => {
  store.dispatch('integrations/get').catch(() => {});
};

const loadIntegration = async () => {
  isLoading.value = true;
  try {
    const { data } = await AsaasIntegrationAPI.get();
    applyHook(data?.hook);
  } catch (error) {
    useAlert(errorMessage(error, t('INTEGRATION_SETTINGS.ASAAS.LOAD_FAILED')), {
      variant: 'error',
    });
  } finally {
    isLoading.value = false;
  }
};

const ensureApiKey = () => {
  if (form.apiKey.trim() || apiKeyConfigured.value) return true;
  useAlert(t('INTEGRATION_SETTINGS.ASAAS.API_KEY_REQUIRED'), {
    variant: 'error',
  });
  return false;
};

const testConnection = async () => {
  if (!ensureApiKey()) return;
  isTesting.value = true;
  try {
    await AsaasIntegrationAPI.validate({
      api_key: form.apiKey.trim(),
      environment: form.environment,
    });
    useAlert(t('INTEGRATION_SETTINGS.ASAAS.TEST_SUCCESS'), {
      variant: 'success',
    });
  } catch (error) {
    useAlert(errorMessage(error, t('INTEGRATION_SETTINGS.ASAAS.TEST_FAILED')), {
      variant: 'error',
    });
  } finally {
    isTesting.value = false;
  }
};

const saveIntegration = async () => {
  if (!ensureApiKey()) return;
  isSaving.value = true;
  try {
    const payload = {
      environment: form.environment,
      enabled: form.enabled,
      features: { ...form.features },
    };
    const apiKey = form.apiKey.trim();
    if (apiKey) payload.api_key = apiKey;

    const { data } = await AsaasIntegrationAPI.save(payload);
    applyHook(data?.hook);
    refreshIntegrationsList();
    useAlert(t('INTEGRATION_SETTINGS.ASAAS.SAVE_SUCCESS'), {
      variant: 'success',
    });
  } catch (error) {
    useAlert(errorMessage(error, t('INTEGRATION_SETTINGS.ASAAS.SAVE_FAILED')), {
      variant: 'error',
    });
  } finally {
    isSaving.value = false;
  }
};

const openRemoveDialog = () => removeDialogRef.value?.open();

const removeIntegration = async () => {
  isRemoving.value = true;
  try {
    await AsaasIntegrationAPI.remove();
    removeDialogRef.value?.close();
    applyHook(null);
    refreshIntegrationsList();
    useAlert(t('INTEGRATION_SETTINGS.ASAAS.REMOVE_SUCCESS'), {
      variant: 'success',
    });
  } catch (error) {
    useAlert(
      errorMessage(error, t('INTEGRATION_SETTINGS.ASAAS.REMOVE_FAILED')),
      { variant: 'error' }
    );
  } finally {
    isRemoving.value = false;
  }
};

onMounted(() => {
  if (isAdmin.value) loadIntegration();
});
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="t('INTEGRATION_SETTINGS.ASAAS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('INTEGRATION_SETTINGS.ASAAS.TITLE')"
        :description="t('INTEGRATION_SETTINGS.ASAAS.DESCRIPTION')"
        :back-button-label="t('INTEGRATION_SETTINGS.ASAAS.BACK')"
      />
    </template>

    <template #body>
      <p
        v-if="!isAdmin"
        class="max-w-3xl rounded-xl border border-n-weak bg-n-solid-1 p-6 text-sm text-n-slate-11"
        data-test="asaas-admin-only"
      >
        {{ t('INTEGRATION_SETTINGS.ASAAS.ADMIN_ONLY') }}
      </p>

      <form
        v-else
        class="flex max-w-3xl flex-col gap-5 rounded-xl border border-n-weak bg-n-solid-1 p-5 sm:p-6"
        data-test="asaas-form"
        @submit.prevent="saveIntegration"
      >
        <div
          class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
        >
          <div class="flex min-w-0 items-center gap-3">
            <BankLogo provider="asaas" />
            <div class="min-w-0">
              <h3 class="text-base font-semibold text-n-slate-12">
                {{ t('INTEGRATION_SETTINGS.ASAAS.TITLE') }}
              </h3>
              <p class="text-xs text-n-slate-11">
                {{
                  isConnected
                    ? validatedAtLabel ||
                      t('INTEGRATION_SETTINGS.ASAAS.STATUS_CONNECTED')
                    : t('INTEGRATION_SETTINGS.ASAAS.STATUS_NOT_CONFIGURED')
                }}
              </p>
            </div>
          </div>
          <span
            class="inline-flex w-fit items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-semibold"
            :class="
              isConnected
                ? 'border-n-teal-7 bg-n-teal-3/20 text-n-teal-10'
                : 'border-n-weak bg-n-alpha-1 text-n-slate-10'
            "
          >
            <span
              class="size-1.5 rounded-full"
              :class="isConnected ? 'bg-n-teal-9' : 'bg-n-slate-7'"
            />
            {{
              isConnected
                ? t('INTEGRATION_SETTINGS.ASAAS.STATUS_CONNECTED')
                : t('INTEGRATION_SETTINGS.ASAAS.STATUS_NOT_CONFIGURED')
            }}
          </span>
        </div>

        <div
          class="flex gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3 text-sm"
        >
          <span
            class="i-hugeicons-shield-01 mt-0.5 size-4 shrink-0 text-n-slate-11"
            aria-hidden="true"
          />
          <div>
            <p class="font-medium text-n-slate-12">
              {{ t('INTEGRATION_SETTINGS.ASAAS.PRIVACY_TITLE') }}
            </p>
            <p class="mt-0.5 text-xs leading-5 text-n-slate-11">
              {{ t('INTEGRATION_SETTINGS.ASAAS.PRIVACY_TEXT') }}
            </p>
          </div>
        </div>

        <Input
          v-model="form.apiKey"
          type="password"
          autocomplete="off"
          show-password-toggle
          data-test="asaas-api-key"
          :label="t('INTEGRATION_SETTINGS.ASAAS.API_KEY_LABEL')"
          :placeholder="
            apiKeyConfigured
              ? t('INTEGRATION_SETTINGS.ASAAS.API_KEY_PLACEHOLDER_CONFIGURED')
              : t('INTEGRATION_SETTINGS.ASAAS.API_KEY_PLACEHOLDER')
          "
          :message="t('INTEGRATION_SETTINGS.ASAAS.API_KEY_HELP')"
        />

        <label class="flex flex-col gap-1.5 text-sm text-n-slate-12">
          <span class="font-medium">{{
            t('INTEGRATION_SETTINGS.ASAAS.ENVIRONMENT_LABEL')
          }}</span>
          <Select
            v-model="form.environment"
            :options="environmentOptions"
            :aria-label="t('INTEGRATION_SETTINGS.ASAAS.ENVIRONMENT_LABEL')"
          />
        </label>

        <fieldset class="flex flex-col gap-2">
          <legend class="mb-2 text-sm font-medium text-n-slate-12">
            {{ t('INTEGRATION_SETTINGS.ASAAS.FEATURES_TITLE') }}
          </legend>
          <div
            v-for="feature in featureOptions"
            :key="feature.key"
            class="flex items-start justify-between gap-4 rounded-lg border border-n-weak p-3"
          >
            <div class="min-w-0">
              <p class="text-sm font-medium text-n-slate-12">
                {{ feature.label }}
              </p>
              <p class="mt-0.5 text-xs leading-5 text-n-slate-11">
                {{ feature.help }}
              </p>
            </div>
            <Switch
              v-model="form.features[feature.key]"
              :data-test="`asaas-feature-${feature.key}`"
              :aria-label="feature.label"
            />
          </div>
        </fieldset>

        <div
          class="flex items-start justify-between gap-4 rounded-lg border border-n-weak p-3"
        >
          <div class="min-w-0">
            <p class="text-sm font-medium text-n-slate-12">
              {{ t('INTEGRATION_SETTINGS.ASAAS.ENABLED_LABEL') }}
            </p>
            <p class="mt-0.5 text-xs leading-5 text-n-slate-11">
              {{ t('INTEGRATION_SETTINGS.ASAAS.ENABLED_HELP') }}
            </p>
          </div>
          <Switch
            v-model="form.enabled"
            data-test="asaas-enabled"
            :aria-label="t('INTEGRATION_SETTINGS.ASAAS.ENABLED_LABEL')"
          />
        </div>

        <div
          class="flex flex-col-reverse gap-2 border-t border-n-weak pt-4 sm:flex-row sm:items-center sm:justify-between"
        >
          <NextButton
            v-if="hook"
            type="button"
            variant="ghost"
            color="ruby"
            icon="i-hugeicons-delete-02"
            :label="t('INTEGRATION_SETTINGS.ASAAS.REMOVE')"
            :disabled="isBusy"
            @click="openRemoveDialog"
          />
          <span v-else />
          <div class="flex flex-col gap-2 sm:flex-row">
            <NextButton
              type="button"
              variant="outline"
              color="slate"
              icon="i-hugeicons-plug-01"
              data-test="asaas-test"
              :label="t('INTEGRATION_SETTINGS.ASAAS.TEST')"
              :is-loading="isTesting"
              :disabled="isBusy"
              @click="testConnection"
            />
            <NextButton
              type="submit"
              data-test="asaas-save"
              :label="t('INTEGRATION_SETTINGS.ASAAS.SAVE')"
              :is-loading="isSaving"
              :disabled="isBusy"
            />
          </div>
        </div>
      </form>

      <Dialog
        ref="removeDialogRef"
        type="alert"
        :title="t('INTEGRATION_SETTINGS.ASAAS.REMOVE_CONFIRM_TITLE')"
        :description="
          t('INTEGRATION_SETTINGS.ASAAS.REMOVE_CONFIRM_DESCRIPTION')
        "
        :confirm-button-label="t('INTEGRATION_SETTINGS.ASAAS.REMOVE_CONFIRM')"
        :cancel-button-label="t('INTEGRATION_SETTINGS.ASAAS.CANCEL')"
        :is-loading="isRemoving"
        @confirm="removeIntegration"
      />
    </template>
  </SettingsLayout>
</template>
